import React, { useState, useEffect, useCallback } from 'react';
import AntTable from 'common_components/AntTable';
import EditButtonRenderer from 'common_components/EditButtonRenderer';
import { dateTimeTimestamptzFormatter } from 'common_components/AntTable/tableUtils';
import TableToolbar from 'common_components/AntTable/Toolbar';
import { push } from 'connected-react-router';
import permission from 'permissions';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentUser } from 'reducers/user';
import { useDeletevehicleTypeChangesMutation } from 'reducers/masterData/VehicleTypeChange/api';
import { useErrorHandler } from 'common_components/ErrorContext';
import { Sorter } from 'utils/sorter';
import styled from 'styled-components';
import { selectTableStateById } from 'reducers/table';
import { message } from 'antd';

const VehicleTypeChangeTableComponent = (props) => {
  const { rowData, isLoading } = props;
  const [dataSource, setDataSource] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [showDelete, setShowDelete] = useState(false);

  const dispatch = useDispatch();
  const { setErrorData } = useErrorHandler();
  const user = useSelector(getCurrentUser);

  const [columns, setColumns] = useState(() => {
    let cols = [
      {
        title: 'edit',
        dataIndex: 'edit',
        align: 'center',
        render: (text, record) => {
          return (
            <EditButtonRenderer data={record} onClick={handleEditClicked} />
          );
        },
        width: 50,
      },
      {
        title: 'Vehicle Type',
        dataIndex: 'vehicle_type',
        dataIndex: ['vehicle_type', 'name'],
        width: 200,
        align: 'center',
        render: (text, record) => {
          return text != null ? text : '-';
        },
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Vehicle Type Change',
        dataIndex: ['vehicle_type_change', 'name'],
        width: 200,
        align: 'center',
        render: (text, record) => {
          return text != null ? text : '-';
        },
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },

      {
        title: 'Created time',
        dataIndex: 'created_at',
        align: 'center',
        width: 150,
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
      {
        title: 'Updated time',
        dataIndex: 'updated_at',
        align: 'center',
        width: 150,
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
    ];

    // Add Show/Hide status from Redux store
    if (tableIdSelector) {
      cols = cols.map((c) => ({
        ...c,
        ...tableIdSelector.find((t) => t.dataIndex === c.dataIndex),
      }));
    }
    return cols;
  });

  useEffect(() => {
    setDataSource(rowData);
  }, [rowData]);

  const [
    deleteVehicleType,
    {
      isSuccess: isSuccessDeleting,
      isUninitialized: isUninitDeleting,
      error: errorDeleting,
      isLoading: isDeleting,
    },
  ] = useDeletevehicleTypeChangesMutation();

  const tableIdSelector = useSelector((state) =>
    selectTableStateById(state, 'group')
  );

  useEffect(() => {
    if (errorDeleting) {
      setErrorData(errorDeleting);
    }
  }, [errorDeleting, setErrorData]);

  useEffect(() => {
    if (!isUninitDeleting && isSuccessDeleting) {
      message.success('Data was successfully deleted');
      clearSelectedRows();
    }
  }, [isSuccessDeleting, isUninitDeleting]);

  const onSearch = useCallback(
    (searchTerm) => {
      if (rowData && rowData.length > 0) {
        let searchCols = columns.filter((c) => c.searchable);
        if (searchCols.length > 0 && searchTerm) {
          let filterData = rowData.filter((d) =>
            searchCols.some(
              (s) =>
                d[s.dataIndex] &&
                d[s.dataIndex]
                  .toString()
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())
            )
          );
          setDataSource(filterData);
        } else {
          setDataSource(rowData);
        }
      }
      clearSelectedRows();
    },
    [columns, rowData]
  );

  const onCreate = () => {
    dispatch(push(`/app/master/vehicle_change/create`));
  };

  const handleEditClicked = (cell, readOnly) => {
    let id = cell.id;
    dispatch(push(`/app/master/vehicle_change/${id}`));
  };

  const onDelete = () => {
    // TODO: Bulk delete
    for (let row of selectedRows) {
      deleteVehicleType({ id: row.id });
    }

    clearSelectedRows();
  };

  const onReload = () => {
    props.refetchData();
    clearSelectedRows();
  };

  const clearSelectedRows = () => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setShowDelete(false);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);

      if (permission.checkPermission(user, 'group', ['d'])) {
        setShowDelete(selectedRows.length > 0 ? true : false);
      }
    },
  };

  return (
    <Container>
      <TableToolbar
        onSearch={onSearch}
        onCreate={onCreate}
        onDelete={onDelete}
        onReload={onReload}
        showDelete={showDelete}
        showReload={true}
        showSearch={true}
        isLoading={isLoading}
        isDeleting={isDeleting}
        showAdd={permission.checkPermission(user, 'group', ['c'])}
        columns={columns}
        setColumns={setColumns}
      />
      <AntTable
        rowKey='id'
        tableId={'group'}
        rowSelection={rowSelection}
        bordered
        showSorterTooltip={false}
        loading={isLoading || isDeleting}
        columns={columns}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        // Below is for right click context menu
        showContextMenu={true}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        menuActions={[
          { label: 'Edit', action: (row) => handleEditClicked(row) },
          {
            label: 'Delete',
            action: (row) => deleteVehicleType({ id: row.id }),
            delete: true,
          },
        ]}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: 0 10px;
`;

export default VehicleTypeChangeTableComponent;
